import React, { useState, useEffect, useRef } from "react";
import "./Events.css";
import EventsTable from "../../Components/EventsTable/EventsTable";
import { useEventType } from "../../hooks/useEventType";
import { useDivision } from "../../hooks/useDivision";
import { useTeam } from "../../hooks/useTeam";
import { useSpendType } from "../../hooks/useSpendType";
import { useSpendTypeActivityTypes } from "../../hooks/useActivityType";
import { motion, AnimatePresence } from "framer-motion";
import { FiFilter, FiX } from "react-icons/fi";
import { EventStatusConstants } from "../../constants/eventStatus";
import TovReport from "../../Components/EventsTable/TovReport";
import { usePermission } from "../../hooks/usePermission";
import { ToastContainer } from "react-toastify";
import EventsReport from "../../Components/EventsTable/EventsReport";
import { ExportEventsPermission } from "../../constants/permissions";

export default function Events() {
  const [dateRange, setDateRange] = useState({
    from: "",
    fromDisplay: "",
    to: "",
    toDisplay: "",
  });
  const [selectedEventType, setSelectedEventType] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedSpendType, setSelectedSpendType] = useState("");
  const [selectedActivityType, setSelectedActivityType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const { data: eventTypes } = useEventType();
  const { data: divisions } = useDivision();
  const { data: teams } = useTeam();
  const { data: spendTypes } = useSpendType();
  const { data: activityTypes } = useSpendTypeActivityTypes(selectedSpendType);

  const filterRef = useRef(null);

  const { data: canExportTOV } = usePermission({
    permissionName: "ExportTOV",
  });

  const [filteredTeams, setFilteredTeams] = useState([]);

  const sortedSpendTypeData = spendTypes?.slice().sort((a, b) => {
    const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
    const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
    return numA - numB;
  });

  const sortedActivityTypeData = activityTypes?.slice().sort((a, b) => {
    const numA = parseInt(a.name.match(/\d+/)?.[0] || 0, 10);
    const numB = parseInt(b.name.match(/\d+/)?.[0] || 0, 10);
    return numA - numB;
  });

  useEffect(() => {
    if (selectedDivision && teams) {
      const teamsInDivision = teams.filter(
        (team) => team.division_id === selectedDivision
      );
      setFilteredTeams(teamsInDivision);
      setSelectedTeam("");
    } else {
      setFilteredTeams([]);
    }
  }, [selectedDivision, teams]);

  useEffect(() => {
    if (!selectedSpendType) {
      setSelectedActivityType("");
    }
  }, [selectedSpendType]);

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    // Store the original date value for display in the input
    const displayDate = value;

    const date = new Date(value);
    const formatted = `${date.getFullYear()}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${date.getDate().toString().padStart(2, "0")}`;

    setDateRange((prev) => ({
      ...prev,
      [name]: formatted,
      [`${name}Display`]: displayDate,
    }));
  };

  const handleEventTypeChange = (e) => {
    setSelectedEventType(e.target.value);
  };

  const handleDivisionChange = (e) => {
    setSelectedDivision(e.target.value);
  };

  const handleTeamChange = (e) => {
    setSelectedTeam(e.target.value);
  };

  const handleSpendTypeChange = (e) => {
    setSelectedSpendType(e.target.value);
    setSelectedActivityType("");
  };

  const handleActivityTypeChange = (e) => {
    setSelectedActivityType(e.target.value);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleClear = () => {
    setDateRange({
      from: "",
      fromDisplay: "",
      to: "",
      toDisplay: "",
    });
    setSelectedEventType("");
    setSelectedDivision("");
    setSelectedTeam("");
    setSelectedSpendType("");
    setSelectedActivityType("");
    setSelectedStatus("");
    setIsFilterOpen(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (dateRange.from) count++;
    if (dateRange.to) count++;
    if (selectedEventType) count++;
    if (selectedDivision) count++;
    if (selectedTeam) count++;
    if (selectedSpendType) count++;
    if (selectedActivityType) count++;
    if (selectedStatus) count++;
    return count;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0;

  const FilterSection = ({ title, children }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-2 mb-2"
    >
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      {children}
    </motion.div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <ToastContainer autoClose={false} closeButton />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Events</h1>
        <div className="flex gap-3">
          {ExportEventsPermission && (
            <EventsReport
              filters={{
                dateRange,
                selectedEventType,
                selectedDivision,
                selectedTeam,
                selectedSpendType,
                selectedActivityType,
                selectedStatus,
              }}
              selectedEvents={selectedEvents}
            />
          )}
          {canExportTOV && (
            <TovReport
              filters={{
                dateRange,
                selectedEventType,
                selectedDivision,
                selectedTeam,
                selectedSpendType,
                selectedActivityType,
                selectedStatus,
              }}
              selectedEvents={selectedEvents}
            />
          )}

          <div className="relative">
            <button
              ref={filterRef}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-blueColor text-white rounded-lg hover:bg-blueColor/90 transition-colors"
            >
              <FiFilter />
              <span>{isFilterOpen ? "Hide Filters" : "Show Filters"}</span>
            </button>
            {hasActiveFilters && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
              >
                {getActiveFiltersCount()}
              </motion.div>
            )}
          </div>
        </div>
      </div>

      <div className="relative">
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0, y: -20 }}
              animate={{ height: "auto", opacity: 1, y: 0 }}
              exit={{ height: 0, opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="absolute top-0 left-0 right-0 z-10 bg-white rounded-xl shadow-lg"
            >
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Date Range Section */}
                  <FilterSection title="Date Range">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          From
                        </label>
                        <input
                          type="date"
                          name="from"
                          value={dateRange.fromDisplay}
                          onChange={handleDateChange}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          To
                        </label>
                        <input
                          type="date"
                          name="to"
                          value={dateRange.toDisplay}
                          onChange={handleDateChange}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                        />
                      </div>
                    </div>
                  </FilterSection>

                  {/* Spend Type & Activity Type Section */}
                  <FilterSection title="Spend Type & Activity Type">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Spend Type
                        </label>
                        <select
                          value={selectedSpendType}
                          onChange={handleSpendTypeChange}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                        >
                          <option value="">All Spend Types</option>
                          {sortedSpendTypeData?.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Activity Type
                        </label>
                        <select
                          value={selectedActivityType}
                          onChange={handleActivityTypeChange}
                          disabled={!selectedSpendType}
                          className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent ${
                            !selectedSpendType
                              ? "bg-gray-100 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <option value="">All Activity Types</option>
                          {sortedActivityTypeData?.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </FilterSection>

                  {/* Division & Team Section */}
                  <FilterSection title="Division & Team">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Division
                        </label>
                        <select
                          value={selectedDivision}
                          onChange={handleDivisionChange}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                        >
                          <option value="">All Divisions</option>
                          {divisions?.map((division) => (
                            <option key={division.id} value={division.id}>
                              {division.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          Team
                        </label>
                        <select
                          value={selectedTeam}
                          onChange={handleTeamChange}
                          disabled={!selectedDivision}
                          className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent ${
                            !selectedDivision
                              ? "bg-gray-100 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <option value="">All Teams</option>
                          {filteredTeams?.map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </FilterSection>

                  {/* Event Type Section */}
                  <FilterSection title="Event Type">
                    <select
                      value={selectedEventType}
                      onChange={handleEventTypeChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                    >
                      <option value="">All Event Types</option>
                      {eventTypes?.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </FilterSection>

                  {/* Status Section */}
                  <FilterSection title="Status">
                    <select
                      value={selectedStatus}
                      onChange={handleStatusChange}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blueColor focus:border-transparent"
                    >
                      <option value="">All Statuses</option>
                      {Object.entries(EventStatusConstants).map(
                        ([key, status]) => (
                          <option key={key} value={key}>
                            {status.text}
                          </option>
                        )
                      )}
                    </select>
                  </FilterSection>
                </div>

                {/* Active Filters */}
                <div className="mt-4 flex flex-wrap gap-2">
                  {dateRange.from && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      From: {dateRange.from}
                      <button
                        onClick={() =>
                          setDateRange((prev) => ({
                            ...prev,
                            from: "",
                            fromDisplay: "",
                          }))
                        }
                      >
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {dateRange.to && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      To: {dateRange.to}
                      <button
                        onClick={() =>
                          setDateRange((prev) => ({
                            ...prev,
                            to: "",
                            toDisplay: "",
                          }))
                        }
                      >
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedEventType && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Event Type:{" "}
                      {
                        eventTypes?.find((t) => t.id === selectedEventType)
                          ?.name
                      }
                      <button onClick={() => setSelectedEventType("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedDivision && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Division:{" "}
                      {divisions?.find((d) => d.id === selectedDivision)?.name}
                      <button onClick={() => setSelectedDivision("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedTeam && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Team: {teams?.find((t) => t.id === selectedTeam)?.name}
                      <button onClick={() => setSelectedTeam("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedSpendType && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Spend Type:{" "}
                      {
                        sortedSpendTypeData?.find(
                          (t) => t.id === selectedSpendType
                        )?.name
                      }
                      <button onClick={() => setSelectedSpendType("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedActivityType && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Activity Type:{" "}
                      {
                        sortedActivityTypeData?.find(
                          (t) => t.id === selectedActivityType
                        )?.name
                      }
                      <button onClick={() => setSelectedActivityType("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                  {selectedStatus && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                    >
                      Status: {EventStatusConstants[selectedStatus]?.text}
                      <button onClick={() => setSelectedStatus("")}>
                        <FiX className="hover:text-blue-600" />
                      </button>
                    </motion.div>
                  )}
                </div>

                {/* Clear Filters Button */}
                <div className="flex justify-end mt-4">
                  <button
                    onClick={handleClear}
                    className="px-4 py-2 text-white hover:bg-gray-600 bg-gray-500 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <FiX />
                    Clear All Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <div className={`transition-all duration-300`}>
        <div className="bg-white rounded-lg shadow-lg">
          <EventsTable
            dateRange={dateRange}
            selectedEventType={selectedEventType}
            selectedDivision={selectedDivision}
            selectedTeam={selectedTeam}
            selectedSpendType={selectedSpendType}
            selectedActivityType={selectedActivityType}
            selectedStatus={selectedStatus}
            onSelectionChange={setSelectedEvents}
          />
        </div>
      </div>
    </div>
  );
}
